import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { devLog } from "../lib/devLog";
import { useAuth } from "../contexts/AuthContext";
import { usePolling } from "../lib/usePolling";
import { Navbar } from "../components/Navbar";
import { KanbanBoard } from "../components/KanbanBoard";
import { TeamTab } from "../components/TeamTab";
import { AnalyticsTab } from "../components/AnalyticsTab";
import { CreateTaskModal } from "../components/CreateTaskModal";
import { TaskDetailModal } from "../components/TaskDetailModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EditorPickerModal } from "../components/EditorPickerModal";
import { ArrowLeft, Plus, Kanban, UsersThree, ChartBar, PencilSimple, Trash, X, Check } from "@phosphor-icons/react";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [doneTotal, setDoneTotal] = useState(0);
  const [doneLimit, setDoneLimit] = useState(50);
  const [loadingMoreDone, setLoadingMoreDone] = useState(false);
  const [tab, setTab] = useState("board");
  const [showCreate, setShowCreate] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [error, setError] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState(false);
  const [editorPickerTask, setEditorPickerTask] = useState(null);
  // Track whether we've ever had a successful load, so the error screen only
  // appears when the INITIAL load never got data. A ref (not state) avoids
  // triggering a re-render / loadAll re-creation.
  const hasLoadedRef = useRef(false);

  const loadAll = useCallback(async (silent = false) => {
    try {
      const [proj, tk, mb] = await Promise.all([
        api.get(`/projects/${id}`),
        // Refetch with the currently-expanded published window so background
        // polling doesn't collapse a "Load more" session back to 50.
        api.get(`/projects/${id}/tasks`, { params: { done_limit: doneLimit } }),
        api.get(`/projects/${id}/members`),
      ]);
      setProject(proj.data);
      setTasks(tk.data.tasks || tk.data);
      if (tk.data.done_total !== undefined) setDoneTotal(tk.data.done_total);
      setMembers(mb.data);
      setError(""); // any success clears a previous transient failure
      hasLoadedRef.current = true;
      // Keep the open task detail modal in sync with the latest data
      setOpenTask((prev) => {
        if (!prev) return prev;
        const list = tk.data.tasks || tk.data;
        const fresh = list.find((t) => t.id === prev.id);
        return fresh || prev;
      });
    } catch (e) {
      devLog.error("[ProjectDetail] poll/load failed", e);
      // A single transient poll failure (network blip / 502) must NOT nuke
      // the page. Only surface an error screen when the INITIAL load never
      // got any data. The next 8s poll recovers on its own.
      if (!silent && !hasLoadedRef.current) {
        setError(formatApiError(e.response?.data?.detail));
      }
    }
  }, [id, doneLimit]);

  const loadMoreDone = useCallback(async () => {
    setLoadingMoreDone(true);
    try {
      const { data } = await api.get(`/projects/${id}/tasks`, {
        params: { done_limit: 50, done_offset: doneLimit },
      });
      const more = data.tasks || data;
      // Merge: dedupe by id (open tasks repeat in the response window)
      setTasks((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        return [...prev, ...more.filter((t) => !seen.has(t.id))];
      });
      if (data.done_total !== undefined) setDoneTotal(data.done_total);
      setDoneLimit((n) => n + 50);
    } catch (e) {
      devLog.error("[ProjectDetail] load more done failed", e);
    }
    setLoadingMoreDone(false);
  }, [id, doneLimit]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live updates: silent background re-fetch every 8s while the tab is
  // visible. A transient failure is ignored — the next poll recovers.
  usePolling(() => loadAll(true), 8000);

  const updateTaskStatus = async (taskId, status, extras = {}) => {
    // Producer/admin routing to "with_editor" — open the editor picker first
    if (status === "with_editor" && extras.skipPicker !== true) {
      const t = tasks.find((x) => x.id === taskId);
      if (t) {
        setEditorPickerTask(t);
        return;
      }
    }
    try {
      const payload = { status };
      if (extras.video_editor_id !== undefined) payload.video_editor_id = extras.video_editor_id;
      const { data } = await api.patch(`/tasks/${taskId}/status`, payload);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const markGfxGiven = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/gfx-given`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const claimAsChecker = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/claim-checker`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const claimAsEditor = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/claim-editor`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const startVideoEdit = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/video-edit-start`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const markVideoEditDone = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/video-edit-done`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (openTask?.id === taskId) setOpenTask(data);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  if (error && !project) {
    return (
      <div>
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/dashboard" className="btn-ghost"><ArrowLeft size={16} /> Back to projects</Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div>
        <Navbar />
        <p className="p-10 text-[color:var(--text-tertiary)]">Loading...</p>
      </div>
    );
  }

  const isProjectAdmin = project.admin_id === user?.id;
  const canCreateTask = user?.role === "admin" || user?.role === "assigner";
  // Task deletion is scoped: only the assigner who created THIS task (or an
  // admin / the project owner) may delete it. Computed per-task below.
  const canDeleteTask = (t) => Boolean(
    t && (user?.role === "admin" || isProjectAdmin || t.assigner_id === user?.id)
  );
  const canDeleteProject = isProjectAdmin || user?.role === "admin";

  const saveProjectName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === project.name) { setEditingName(false); return; }
    try {
      const { data } = await api.patch(`/projects/${project.id}`, { name: trimmed });
      setProject(data);
      setEditingName(false);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteProject = async () => {
    try {
      await api.delete(`/projects/${project.id}`);
      navigate("/dashboard");
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const tabs = [
    { id: "board", label: "Board", icon: Kanban },
    { id: "team", label: "Team", icon: UsersThree },
    { id: "analytics", label: "Analytics", icon: ChartBar },
  ];

  return (
    <div>
      <Navbar />
      <main className="max-w-[1720px] mx-auto px-6 py-8">
        <Link to="/dashboard" className="label-mono inline-flex items-center gap-2 hover:text-white mb-4" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> All projects
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="flex-1 min-w-0">
            <p className="label-mono mb-2">Project</p>
            {editingName ? (
              <div className="flex items-center gap-2 max-w-xl">
                <input
                  data-testid="project-name-edit-input"
                  className="input font-heading text-2xl font-bold tracking-tight"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") saveProjectName(); if (e.key === "Escape") setEditingName(false); }}
                />
                <button data-testid="save-project-name" onClick={saveProjectName} className="btn-primary"><Check size={16} weight="bold" /></button>
                <button data-testid="cancel-project-name" onClick={() => setEditingName(false)} className="btn-ghost"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none" data-testid="project-name-heading">{project.name}</h1>
                {isProjectAdmin && (
                  <button
                    data-testid="edit-project-name-button"
                    onClick={() => { setNameDraft(project.name); setEditingName(true); }}
                    className="text-[color:var(--text-tertiary)] hover:text-white transition p-1"
                    aria-label="Edit name"
                  >
                    <PencilSimple size={18} />
                  </button>
                )}
              </div>
            )}
            {project.description && !editingName && (
              <p className="text-[color:var(--text-secondary)] mt-3 max-w-2xl">{project.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {tab === "board" && canCreateTask && (
              <button data-testid="open-create-task-button" onClick={() => setShowCreate(true)} className="btn-primary">
                <Plus size={16} weight="bold" /> New task
              </button>
            )}
            {canDeleteProject && (
              <button data-testid="delete-project-button" onClick={() => setConfirmDeleteProject(true)} className="btn-danger" title="Delete project">
                <Trash size={14} /> <span className="hidden sm:inline">Delete project</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex border-b border-[color:var(--border)] mb-6">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 font-heading text-sm font-bold tracking-wide uppercase transition border-b-2 -mb-px ${
                  active
                    ? "text-white border-[#007AFF]"
                    : "text-[color:var(--text-tertiary)] border-transparent hover:text-white"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "board" && (
          <KanbanBoard
            tasks={tasks}
            members={members}
            currentUser={user}
            onStatusChange={updateTaskStatus}
            onOpenTask={async (t) => {
              // Show the slim card data immediately; then hydrate the full
              // task (status_history + description) in the background.
              setOpenTask(t);
              try {
                const { data } = await api.get(`/tasks/${t.id}`);
                setOpenTask((cur) => (cur && cur.id === data.id ? data : cur));
              } catch (_) { /* keep slim task on error */ }
            }}
            onGfxGiven={markGfxGiven}
            onClaimChecker={claimAsChecker}
            onClaimEditor={claimAsEditor}
            onStartVideoEdit={startVideoEdit}
            onVideoEditDone={markVideoEditDone}
            doneTotal={doneTotal}
            onLoadMoreDone={loadMoreDone}
            loadingMoreDone={loadingMoreDone}
          />
        )}
        {tab === "team" && (
          <TeamTab
            project={project}
            members={members}
            isProjectAdmin={isProjectAdmin}
            onUpdate={loadAll}
          />
        )}
        {tab === "analytics" && <AnalyticsTab projectId={id} members={members} />}
      </main>

      {showCreate && (
        <CreateTaskModal
          projectId={id}
          members={members}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAll(); }}
        />
      )}
      {openTask && (
        <TaskDetailModal
          task={openTask}
          members={members}
          currentUser={user}
          onClose={() => setOpenTask(null)}
          onStatusChange={updateTaskStatus}
          onDelete={() => setConfirmDeleteTask(true)}
          onGfxGiven={() => markGfxGiven(openTask.id)}
          onClaimChecker={() => claimAsChecker(openTask.id)}
          onClaimEditor={() => claimAsEditor(openTask.id)}
          onStartVideoEdit={() => startVideoEdit(openTask.id)}
          onVideoEditDone={() => markVideoEditDone(openTask.id)}
          onTaskUpdate={(updated) => {
            setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setOpenTask(updated);
          }}
          isProjectAdmin={isProjectAdmin}
          canDelete={canDeleteTask(openTask)}
        />
      )}
      <ConfirmDialog
        open={confirmDeleteProject}
        title="Delete this project?"
        message="All tasks, invites, and members for this project will be permanently removed. This cannot be undone."
        confirmLabel="Delete project"
        onCancel={() => setConfirmDeleteProject(false)}
        onConfirm={async () => { setConfirmDeleteProject(false); await deleteProject(); }}
      />
      <ConfirmDialog
        open={confirmDeleteTask}
        title="Delete this task?"
        message="This task and its history will be permanently removed."
        confirmLabel="Delete task"
        onCancel={() => setConfirmDeleteTask(false)}
        onConfirm={async () => {
          setConfirmDeleteTask(false);
          if (!openTask) return;
          try {
            await api.delete(`/tasks/${openTask.id}`);
            setTasks((prev) => prev.filter((t) => t.id !== openTask.id));
            setOpenTask(null);
          } catch (e) {
            alert(formatApiError(e.response?.data?.detail));
          }
        }}
      />
      {editorPickerTask && (
        <EditorPickerModal
          task={editorPickerTask}
          members={members}
          onClose={() => setEditorPickerTask(null)}
          onConfirm={async (chosenId) => {
            const taskId = editorPickerTask.id;
            setEditorPickerTask(null);
            await updateTaskStatus(taskId, "with_editor", {
              video_editor_id: chosenId,
              skipPicker: true,
            });
          }}
        />
      )}
    </div>
  );
}
